import type { BrandVariants } from '@fluentui/react-components';

/**
 * The Bedrock brand ramp.
 *
 * Fluent derives every brand-related token — backgrounds, borders, hover and
 * pressed states, foregrounds on light and dark surfaces — from these sixteen
 * stops, assuming a specific perceptual-lightness progression.
 *
 * TWO STOPS ARE ANCHORED TO REAL BRAND COLOURS and must not be moved:
 *
 *   80  #A85400 — the application header. White text on it measures 5.34:1,
 *                 which passes WCAG AA. Fluent maps stop 80 to
 *                 `colorBrandBackground` in the light theme, so the header can
 *                 use the token rather than a hard-coded hex.
 *   120 #FD991D — the Bedrock orange, as used throughout rock-software.
 *
 * Those placements are not arbitrary. Converting Fluent's own reference ramp to
 * CIE L*C*h and matching lightness at an orange hue puts #A85400 at L* 45.2
 * (reference stop 80 is 44.9) and #FD991D at L* 72.1 (stop 120 is 72.6). The
 * remaining stops interpolate between and beyond them, with chroma capped to
 * the sRGB gamut at each lightness.
 *
 * ⚠ STOPS 100 AND ABOVE MUST NEVER BE USED AS A FOREGROUND IN THE LIGHT THEME.
 * #FD991D on white is only 2.15:1 — it fails AA and even AA-large. Fluent's
 * `createLightTheme` never maps them that way, so you are safe by construction;
 * but hand-writing `color: tokens.colorBrandBackground2Pressed` (or similar)
 * would silently produce unreadable text. Reach for stop 70/80 tokens instead
 * (`colorBrandForeground1`, `colorBrandForegroundLink`).
 */
export const brand: BrandVariants = {
  10: '#050302',
  20: '#1F150F',
  30: '#3A1E10',
  40: '#4F260F',
  50: '#642F0D',
  60: '#783A0C',
  70: '#8B4408',
  80: '#A85400', // anchor — application header; white on it = 5.34:1 (AA)
  90: '#C56C14',
  100: '#E2831F',
  110: '#EF9026',
  120: '#FD991D', // anchor — the Bedrock orange
  130: '#FBB364',
  140: '#FBC995',
  150: '#FBDDBD',
  160: '#FCF0E4',
};
